import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsNavComponent } from '../settings-nav/settings-nav';
import { BrnService, BrnPlanRequest } from '../../services/brn.service';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, SettingsNavComponent],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  botId: string = '';

  // Plan properties
  planLoading: boolean = false;
  currentMonthDate: string = '';
  planRevenue: number = 0;
  planTotalBudget: number = 0;
  planMetaBudget: number = 0;
  planGoogleBudget: number = 0;

  private planSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private brnService: BrnService
  ) {}

  ngOnInit(): void {
    this.botId = this.route.snapshot.paramMap.get('bot_id') || '';
    this.currentMonthDate = this.getFirstDayOfCurrentMonth();

    this.planSubject
      .pipe(debounceTime(600), takeUntil(this.destroy$))
      .subscribe(() => {
        this.savePlan();
      });

    this.loadPlan();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Inline editing handlers
  onKpiInput(event: Event, field: 'revenue' | 'total' | 'meta' | 'google') {
    const input = event.target as HTMLInputElement;
    const value = this.parseCurrency(input.value);

    switch (field) {
      case 'revenue':
        this.planRevenue = value;
        break;
      case 'total':
        this.planTotalBudget = value;
        break;
      case 'meta':
        this.planMetaBudget = value;
        break;
      case 'google':
        this.planGoogleBudget = value;
        break;
    }
    // Reformat for display
    input.value = this.formatCurrencyWithCents(value);
    this.planSubject.next();
  }

  // Data
  private getFirstDayOfCurrentMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  }

  async loadPlan() {
    try {
      this.planLoading = true;
      const response = await firstValueFrom(
        this.brnService.getPlan(this.botId, this.currentMonthDate)
      );
      if (response?.status && response.plan) {
        this.planRevenue = response.plan.revenue?.total || 0;
        this.planTotalBudget = response.plan.budget?.total || 0;
        this.planMetaBudget = response.plan.budget?.channels?.['meta-ads'] || 0;
        this.planGoogleBudget = response.plan.budget?.channels?.['google-ads'] || 0;
      } else {
        this.planRevenue = 0;
        this.planTotalBudget = 0;
        this.planMetaBudget = 0;
        this.planGoogleBudget = 0;
      }
    } catch (e) {
      this.planRevenue = 0;
      this.planTotalBudget = 0;
      this.planMetaBudget = 0;
      this.planGoogleBudget = 0;
    } finally {
      this.planLoading = false;
    }
  }

  async savePlan() {
    try {
      const planRequest: BrnPlanRequest = {
        budget: {
          total: this.planTotalBudget || 0,
          channels: {
            'meta-ads': this.planMetaBudget || 0,
            'google-ads': this.planGoogleBudget || 0
          }
        },
        revenue: {
          total: this.planRevenue || 0
        }
      };
      await firstValueFrom(
        this.brnService.setPlan(this.botId, this.currentMonthDate, planRequest)
      );
    } catch (e) {
      // swallow for now; UX could add toast
    }
  }

  // Formatting helpers
  formatCurrencyWithCents(value: number): string {
    const num = Number.isFinite(value) ? value : 0;
    return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  parseCurrency(text: string): number {
    if (!text) return 0;
    const clean = text.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(clean);
    if (!isFinite(parsed)) return 0;
    return Math.round(parsed * 100) / 100;
  }
}


